import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {TextView} from '../../ui_canvas_engine/TextView'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {UIGameEndReturnToMapButtonController} from './UIGameEndReturnToMapButtonController'
import {UIGameEndStatsController} from './UIGameEndStatsController'
import {UIGameEndFooterController} from './UIGameEndFooterController'
import {GameEndTimeAndTurnsView} from './GameEndTimeAndTurnsView'
import {Sound} from '../../Sound'
import {UIGameManager} from '../UIGameManager'
import {UIGameEndItemBoxController} from './UIGameEndItemBoxController'
import {StorageWrapper} from '../../StorageWrapper'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {UIGameEndShadeWithLineController} from './UIGameEndShadeWithLineController'
import {ConfettiAnimationWrapper} from '../ui_game_animations/ConfettiAnimationWrapper'
import {TranslationObject} from '@colonist/i18n'
import {UIGameMechanic} from '../UIGameEventController'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {BeginnerModeActionState} from '@colonist/game-state-types'
import {UIGameEndShareButtonController} from './UIGameEndShareButtonController'
import {ViewPopupEndgameSignup} from '../../ui_popup/ViewPopupEndgameSignup'
import {ClientUserState} from '../../ClientUserState'
import {EndGamePlayerState, EndGameState, PlayerColor} from '@colonist/game-states'
import {EndGameRankedUserState, EndGameStateUtils} from '@colonist/game-endgame-types'
import {RankedUserStateController} from '@colonist/leaderboard-types'
import {CA} from '../../analytics/Analytics'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {socket} from '../../EntryShared'

export interface HideableEndGameSection {
    show(): void
    hide(): void
}

export abstract class UIGameEndController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    protected readonly endGameState: EndGameState
    protected readonly isReplayAvailable: boolean
    protected readonly winnerColor: PlayerColor
    readonly rankedUserStates: EndGameRankedUserState[] | undefined
    protected readonly isShownAfterLiveGame: boolean

    protected mainContainer: SectionBgView
    protected contentContainer: SectionBgView
    protected sectionSize: number

    protected logo: SpriteView
    protected title: TextView

    protected timeTurns: GameEndTimeAndTurnsView
    returnToMapButton: UIGameEndReturnToMapButtonController
    protected shareButton: UIGameEndShareButtonController
    protected statsController: UIGameEndStatsController
    protected footerController: UIGameEndFooterController
    protected shadeWithLine: UIGameEndShadeWithLineController
    itemBoxController: UIGameEndItemBoxController | undefined

    protected abstract extraBuildSteps()

    constructor(uiGameManager: UIGameManager, endGameState: EndGameState, isReplayAvailable: boolean, rankedUserStates: EndGameRankedUserState[] | undefined, isShownAfterLiveGame: boolean | undefined) {
        this.uiGameManager = uiGameManager
        this.winnerColor = EndGameStateUtils.getPlayersSortedByRank(endGameState.players)[0].color
        this.isShownAfterLiveGame = isShownAfterLiveGame ?? true

        const isRankedUserStateDefined = rankedUserStates != undefined && rankedUserStates.length != 0
        if(isRankedUserStateDefined) {
            const deserializedRankedUserState: EndGameRankedUserState[] = []
            for(const rankedUserState of rankedUserStates) {
                deserializedRankedUserState.push({
                    color: rankedUserState.color,
                    rankedState: RankedUserStateController.deserialize(rankedUserState.rankedState),
                })
            }

            this.rankedUserStates = deserializedRankedUserState
        }

        this.endGameState = endGameState
        this.isReplayAvailable = isReplayAvailable

        this.build()
    }

    protected build() {
        const canvas = this.uiGameManager.canvasController
        this.mainContainer = new SectionBgView(0, 0, canvas.appW - canvas.chatWidth, canvas.appH, canvas.app.stage, true)
        this.mainContainer.drawBg()

        this.showVictoryConfetti()

        this.sectionSize = this.mainContainer.height / 6

        this.contentContainer = new SectionBgView(0, 0, this.mainContainer.width, this.mainContainer.height, this.mainContainer)

        this.createTitle()
        this.playVictorySound()
        this.createTimeAndTurns()
        this.createReturnToMapButton()
        this.createShareButton()
        this.createStatsController()
        this.createFooter()
        this.createShadeWithLine()
        if(!this.uiGameManager.gameController.isReplay) this.createEndgameRankedScreen()

        UIGameEndController.showSignupPopup()

        this.uiGameManager.eventController.registerUIGameMechanic(this)

        this.extraBuildSteps()
        this.setVisible(true)
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums) {
        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.BeginnerModeLevelEnd: this.onBeginnerModeLevelEnd(data); break
            case GameStateEnums.UIGameEventEnums.RebuildingGameUI: this.itemBoxController?.setItemBoxVisible(false); break
            default: break
        }
    }

    protected abstract createTitle()
    protected abstract createTimeAndTurns()
    protected abstract createReturnToMapButton()
    protected abstract createShareButton()
    protected abstract createStatsController()
    protected abstract createFooter()

    protected createShadeWithLine() {
        const canvas = this.uiGameManager.canvasController
        const y = this.statsController.container.y + this.statsController.tabButtonsView.height

        let h = this.statsController.container.height
        h += this.footerController.container.height

        this.shadeWithLine = new UIGameEndShadeWithLineController(canvas, y, h, this.mainContainer)
        this.mainContainer.addChildAt(this.shadeWithLine.container, 1)
    }

    protected abstract createEndgameRankedScreen()

    private playVictorySound() {
        // TODO Eventually replace the victory sound with the end game music and play for all players
        // Sound.playSound(Sound.SoundType.MusicEgoSum)

        if(!this.isPlayerWinner) return
        if(this.uiGameManager.gameController.isReplay) return

        Sound.playSound(Sound.SoundType.Victory)
    }

    private onBeginnerModeLevelEnd(data: BeginnerModeActionState) {
        this.statsController.hide()
        this.itemBoxController?.setItemBoxVisible(false)
        StorageWrapper.incrementBeginnerModeLevel()

        if(!this.isPlayerWinner) {
            this.timeTurns.timeAndTurnsTextView.setText({key: 'strings:game.beginnerMode.lost'})
            this.timeTurns.timeAndTurnsTextView.centerX()

            this.title.setText({key: 'strings:game.beginnerMode.opponentWon'})
            this.title.centerX()
        }
    }

    private showVictoryConfetti() {
        if(!this.isPlayerWinner) return
        if(this.uiGameManager.gameController.isReplay) return

        const canvas = this.uiGameManager.canvasController
        const width = canvas.appW - canvas.chatWidth
        const confettiX = width / 2
        const confettiY = 0
        const confettiDuration = 10_000
        new ConfettiAnimationWrapper(this.uiGameManager, this.uiGameManager.canvasController.app.stage, confettiX, confettiY, confettiDuration)
    }

    protected getEndGameText(): TranslationObject {
        const myColor = this.uiGameManager.gameController.myColor

        const isSpectator = myColor == PlayerColor.None
        if(isSpectator) return {key: 'strings:game.endGame.rank.spectator'}

        switch(this.endGameState.players[myColor]?.rank) {
            case 1: return {key: 'strings:game.endGame.rank.1st'}
            case 2: return {key: 'strings:game.endGame.rank.2nd'}
            case 3: return {key: 'strings:game.endGame.rank.3rd'}
            case 4: return {key: 'strings:game.endGame.rank.4th'}
            default: return {key: 'strings:game.endGame.rank.defeat'}
        }
    }

    protected abstract animate()

    private static showSignupPopup() {
        if(UIUtilsEjsVariables.isUserOnDiscord) return
        if(UIUtilsEjsVariables.isUserOnMobileApp) return
        if(ClientUserState.userState?.isLoggedIn) return

        const gameCountMilestones = [5, 10, 25]
        const postMilestonesInterval = 50
        const completedGameCount = (ClientUserState.userState?.totalCompletedGameCount ?? 0) + 1
        const isHardCodedGameCount = gameCountMilestones.includes(completedGameCount)
        const passedTheHardcodedGameCounts = completedGameCount > Math.max(...gameCountMilestones)
        const hitMilestoneInterval = completedGameCount % postMilestonesInterval == 0

        const shouldShowSignupPopup = isHardCodedGameCount || (passedTheHardcodedGameCounts && hitMilestoneInterval)
        if(!shouldShowSignupPopup) return

        const popup = new ViewPopupEndgameSignup(completedGameCount)
        popup.show()
    }

    setVisible(state: boolean) {
        this.mainContainer.visible = state

        if(state) {
            this.statsController.showActiveView()
            this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.GameEndHideMap)
        } else {
            this.statsController.hideActiveView()
            this.uiGameManager.eventController.sendEvent(undefined, UIGameEventEnums.GameEndShowMap)
        }
    }

    getPlayerRankedState(player: PlayerColor): EndGameRankedUserState | undefined {
        if(this.rankedUserStates == undefined) return undefined
        for(const rankedUserState of this.rankedUserStates) {
            if(rankedUserState.color == player) return rankedUserState
        }

        return undefined
    }

    destroy() {
        this.mainContainer.destroy({children: true})
        this.uiGameManager.eventController.unregisterUIGameMechanic(this)
    }

    get isVisible(): boolean {
        return this.mainContainer.visible
    }

    protected get endGamePlayerState(): EndGamePlayerState | undefined {
        return this.endGameState.players[this.uiGameManager.gameController.myColor]
    }

    private get isPlayerWinner(): boolean {
        const players = Object.values(this.endGameState.players)
        for(const endGameState of players) {
            if(endGameState.winningPlayer == false) continue
            if(endGameState.color != this.uiGameManager.gameController.myColor) continue
            return true
        }
        return false
    }

    // eslint-disable-next-line class-methods-use-this
    private logSocketAnalytics() {
        const stats = {...socket.monitor.getFinalStats()}
        for(const statsKey in stats) {
            if(stats[statsKey] == 0) delete stats[statsKey]
        }

        void CA.sendAnalyticEventOnce({
            category: AnalyticEventCategory.Socket,
            type: AnalyticEventType.SocketStatsAfterGame,
            stringValue: JSON.stringify(stats),
        })
    }
}
